<template>
  <div class="buyer-product-details-page market-project market-project-v2">
    <div class="seller-products-page__layout">
      <div class="accFeed__wrapper matchmaking__wrapper">
        <div class="accFeed__sidebar matchmaking__sidebar">
          <div class="sidebar-block">
            <a class="project-details__back" href="#" @click.prevent="goBack">
              <img src="@/assets/images/icon-back.svg" alt="" class="project-details__back-icon" />
              {{ backLabel }}
            </a>
          </div>
        </div>

        <div class="accFeed__content matchmaking__content">
          <div class="project-details">
            <div class="project-details__wrapper">
              <SuccessMessage v-if="successText" :text="successText" />

              <div class="project-details__header">
                <h1 class="project-details__title">
                  {{ productTitle }}
                  <MarketplaceUnreadBadge :count="productBulbCount" />
                </h1>
              </div>

              <span class="project-details__deadline">
                Offer deadline: <strong>{{ offerDeadlineText }}</strong>.
                <br>
                Counter offers allowed: <strong>{{ offersAllowedText }}</strong>
              </span>

              <section class="make-offer-content">
                <div class="form-col-2">
                  <div class="form-col-1">
                    <label>Marketplace</label>
                    <span class="field">{{ marketplaceName }}</span>
                  </div>
                  <div class="form-col-1">
                    <label>Delivery date</label>
                    <span class="field">{{ deliveryDateText }}</span>
                  </div>
                </div>

                <div class="form-col-2">
                  <div class="form-col-1">
                    <label>Price, euro</label>
                    <span class="field">{{ priceText }}</span>
                  </div>
                </div>

                <div class="form-col-2">
                  <div class="form-col-1">
                    <label>Company Name</label>
                    <span class="field">{{ safeText(product.company_name) }}</span>
                  </div>
                  <div class="form-col-1">
                    <label>Project Country</label>
                    <span class="field">{{ companyCountryText }}</span>
                  </div>
                </div>

                <div v-if="product.company_address" class="form-col-1 location mb-2">
                  <label>Project Location</label>
                  <span class="field">{{ safeText(product.company_address) }}</span>
                </div>

                <div
                  v-for="(pair, idx) in gridFieldsPairs"
                  :key="`pair-${idx}`"
                  class="form-col-2"
                >
                  <div v-if="pair[0]" class="form-col-1">
                    <label>{{ pair[0].name }}</label>
                    <span class="field">{{ formatFieldValue(pair[0]) }}</span>
                  </div>
                  <div v-if="pair[1]" class="form-col-1">
                    <label>{{ pair[1].name }}</label>
                    <span class="field">{{ formatFieldValue(pair[1]) }}</span>
                  </div>
                </div>

                <div class="form-col-1 location mb-2">
                  <label>Product details</label>
                  <span class="field">{{ safeText(product.detail) }}</span>
                </div>

                <div class="form-col-1 location mb-2">
                  <label>Product advantages/features</label>
                  <span class="field">{{ safeText(product.feature) }}</span>
                </div>

                <div
                  v-if="documents.length || images.length || videos.length"
                  class="addmedia"
                >
                  <div class="addmedia__downloaded">
                    <h5 class="addmedia__downloaded-title">Presentation and stuff</h5>

                    <div v-if="documents.length">
                      <h6>Documents ({{ documents.length }}):</h6>
                      <div
                        v-for="(doc, idx) in documents"
                        :key="`doc-${idx}`"
                        class="addmedia__downloaded-item"
                      >
                        <span class="icon-pdf"></span>
                        <a
                          class="addmedia__downloaded-name"
                          :href="doc.url"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {{ doc.title || doc.name || doc.url }}
                        </a>
                      </div>
                    </div>

                    <div v-if="images.length">
                      <h6>Images ({{ images.length }}):</h6>
                      <ul class="media-list">
                        <li
                          v-for="(img, idx) in images"
                          :key="`img-${idx}`"
                          class="media-item"
                        >
                          <img :src="img.url" :alt="img.title || img.name || 'image'">
                          <span>{{ img.title || img.name || img.url }}</span>
                        </li>
                      </ul>
                    </div>

                    <div v-if="videos.length">
                      <h6>Video ({{ videos.length }}):</h6>
                      <ul class="media-list">
                        <li
                          v-for="(video, idx) in videos"
                          :key="`video-${idx}`"
                          class="media-item"
                        >
                          <iframe
                            :src="video.url"
                            :title="video.title || 'video'"
                            frameborder="0"
                            allow="
                              accelerometer;
                              autoplay;
                              clipboard-write;
                              encrypted-media;
                              gyroscope;
                              picture-in-picture;
                              web-share
                            "
                            allowfullscreen
                          ></iframe>
                          <span>{{ video.title || video.name || video.url }}</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div class="market-project__actions flex-end" data-panel-group>
                  <div class="market-project__buttons buttons-group">
                    <a
                      href="#"
                      class="btn-action"
                      data-toggle-panel="seller"
                      :data-panel-unread="productMessagesCount"
                      role="button"
                    >
                      <i class="icon-chat"></i>
                      Talk to Seller
                      <MarketplaceUnreadBadge :count="productMessagesCount" />
                    </a>
                    <a
                      href="#"
                      class="btn-action"
                      role="button"
                      @click.prevent="toggleSaved"
                    >
                      <i :class="isSaved ? 'icon-unsave' : 'icon-save'"></i>
                      {{ isSaved ? 'Unsave' : 'Save' }}
                    </a>
                    <a
                      href="#"
                      class="btn-action"
                      data-toggle-panel="offer"
                      :data-panel-unread="counterOfferBulbCount"
                      role="button"
                    >
                      <i class="icon-coffer"></i>
                      Counter offers
                      <MarketplaceUnreadBadge :count="counterOfferBulbCount" />
                    </a>
                    <a
                      v-if="!isProductApproved"
                      href="#"
                      class="btn-action btn-approve"
                      :class="{ disabled: !canApproveProduct }"
                      :aria-disabled="String(!canApproveProduct)"
                      role="button"
                      @click.prevent="onApproveClick"
                    >
                      <i class="icon-approve"></i>
                      {{ isApproving ? 'Approving...' : 'Approve' }}
                    </a>
                  </div>

                  <div class="action-panel" data-panel="seller">
                    <button data-close-panel>Close ✕</button>

                    <div class="action-panel__content">
                      <MessageThread
                        ref="productMessageThread"
                        :messages="messages"
                        :isSending="isSendingProductMessage"
                        :meAvatar="meAvatar"
                        :submitOnEnter="true"
                        placeholder="Comment..."
                        @send="sendProductMessage"
                        @like="handleLikeProductMessage"
                        @unlike="handleUnlikeProductMessage"
                      />
                    </div>
                  </div>

                  <div class="action-panel" data-panel="offer">
                    <button data-close-panel>Close ✕</button>

                    <div class="action-panel__content">
                      <div class="coffers">
                        <h3 class="mb-2">Your counter offer(s):</h3>

                        <ul v-if="counterOffers.length" class="coffers__list">
                          <li
                            v-for="(offer, idx) in counterOffers"
                            :key="offer.id || idx"
                            class="coffers__item"
                          >
                            <div class="form-block__body">
                              <div class="form-row-grid">
                                <label>Price:</label>
                                <span>
                                  <strong>{{ formatOfferPrice(offer) }}</strong>
                                  <MarketplaceUnreadBadge :count="offerBulb(offer)" />
                                </span>
                              </div>

                              <div class="form-row-grid">
                                <label>Terms:</label>
                                <div class="text">
                                  <p
                                    v-for="(p, pIdx) in offerTermsParagraphs(offer)"
                                    :key="pIdx"
                                  >
                                    {{ p }}
                                  </p>
                                </div>
                              </div>

                              <p v-if="offer['__detailsLoading']" class="mb-2 buyer-product-details__hint">
                                Loading messages...
                              </p>

                              <p v-if="offer['__detailsError']" class="mb-2 buyer-product-details__error">
                                {{ offer['__detailsError'] }}
                              </p>

                              <MessageThread
                                :ref="`offerThread-${offer.id}`"
                                :messages="offer.messages"
                                :isSending="sendingOfferId === offer.id"
                                :meAvatar="meAvatar"
                                :submitOnEnter="true"
                                placeholder="Comment..."
                                @send="sendOfferMessage(offer, $event)"
                                @like="(msg) => handleLikeOfferMessage(offer, msg)"
                                @unlike="(msg) => handleUnlikeOfferMessage(offer, msg)"
                              />
                            </div>
                          </li>
                        </ul>

                        <div v-else class="form-block__body">
                          <p class="mb-0">No counter offers yet.</p>
                        </div>
                      </div>

                      <h3
                        v-if="!isCounterOfferCreationLocked"
                        class="mb-2 mt-4"
                      >
                        Submit counter offer
                      </h3>

                      <div
                        v-if="!isCounterOfferCreationLocked"
                        class="form-block__body"
                      >
                        <div class="form-row-grid">
                          <label>Your price:</label>
                          <input
                            v-model="counterOfferPrice"
                            class="form-control"
                            type="number"
                            min="0"
                            step="0.01"
                          >
                        </div>

                        <div class="form-row-grid">
                          <label>Your terms:</label>
                          <textarea
                            v-model.trim="counterOfferTerms"
                            class="form-control"
                            rows="4"
                          ></textarea>
                        </div>

                        <div class="btns-group--right">
                          <button
                            class="btn btn-primary"
                            type="button"
                            :disabled="isSendingCounterOffer || !canSubmitCounterOffer"
                            @click.prevent="submitCounterOffer"
                          >
                            {{ isSendingCounterOffer ? 'Submitting...' : 'Submit' }}
                          </button>
                        </div>

                        <p v-if="counterOfferSuccessMessage" class="mt-2 buyer-product-details__success">
                          {{ counterOfferSuccessMessage }}
                        </p>

                        <p v-if="counterOfferErrorMessage" class="mt-2 buyer-product-details__error">
                          {{ counterOfferErrorMessage }}
                        </p>
                      </div>

                      <p
                        v-else
                        class="mt-2 buyer-product-details__hint"
                      >
                        New counter offers are unavailable after approval.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            </div>
          </div>

          <div v-if="isLoading" class="d-flex justify-content-center p-2">
            <div class="loader"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-underscore-dangle */
import moment from 'moment';

import MatchmakingService from '@/services/MatchmakingService';
import exception from '@/mixins/exception';
import MessageThread from '@/components/matchmaking/MessageThread.vue';
import MarketplaceUnreadBadge from '@/components/matchmaking/MarketplaceUnreadBadge.vue';
import SuccessMessage from '@/components/matchmaking/SuccessMessage.vue';
import marketplaceOfferDetails from '@/mixins/marketplaceOfferDetails';
import marketplaceProductFromResponse from '@/helpers/marketplaceProductResponse';

export default {
  name: 'BuyerProductDetailsPage',
  components: {
    MessageThread,
    MarketplaceUnreadBadge,
    SuccessMessage,
  },
  mixins: [exception, marketplaceOfferDetails],
  data() {
    return {
      isLoading: false,
      product: {},
      successText: '',
      successTimer: null,
      messages: [],
      isSendingProductMessage: false,
      counterOffers: [],
      counterOfferPrice: '',
      counterOfferTerms: '',
      isSendingCounterOffer: false,
      counterOfferSuccessMessage: '',
      counterOfferErrorMessage: '',
      counterOfferMessageTimerId: null,
      sendingOfferId: null,
      isApproving: false,
      productThreadPromise: null,
      productThreadPending: false,
      isProductThreadSyncing: false,
      pendingProductPanelOpenDetail: null,
      isOfferPanelSyncing: false,
      pendingOfferPanelOpenDetail: null,
    };
  },
  computed: {
    offerListKey() {
      return 'counterOffers';
    },
    currentView() {
      return String(this.$route.query.view || 'products');
    },
    backLabel() {
      return this.currentView === 'saved-products'
        ? 'Back to Saved Products'
        : 'Back to Product Search';
    },
    productId() {
      return this.product?.id;
    },
    productTitle() {
      return this.product?.name || '—';
    },
    productMsgIds() {
      return (Array.isArray(this.messages) ? this.messages : [])
        .map((m) => m && m.id)
        .filter((id) => id !== undefined && id !== null);
    },
    productBulbCount() {
      return this.$store.getters.productBulb('buyer', this.productId);
    },
    counterOfferBulbCount() {
      return (Array.isArray(this.counterOffers) ? this.counterOffers : []).reduce(
        (total, offer) => total
          + this.$store.getters.productOfferBulb('buyer', offer && offer.id),
        0,
      );
    },
    productMessagesCount() {
      const total = Number(this.productBulbCount) || 0;
      const offers = Number(this.counterOfferBulbCount) || 0;
      const residual = Math.max(0, total - offers);
      const loaded = Number(
        this.$store.getters.productMessageBulb('buyer', this.productMsgIds),
      ) || 0;
      const live = Number(
        this.$store.getters.productLiveMessageBulb('buyer', this.productId),
      ) || 0;
      return Math.max(residual, loaded, live);
    },
    marketplaceName() {
      return this.product?.marketplace?.name || '—';
    },
    priceText() {
      const hasPrice = this.product && this.product.price !== undefined
        && this.product.price !== null;
      if (hasPrice && String(this.product.price).trim() !== '') return String(this.product.price);
      return '—';
    },
    companyCountryText() {
      const cc = this.product?.company_country;
      if (!cc) return '—';
      if (typeof cc === 'object') return cc.name || cc.iso || '—';
      return String(cc);
    },
    offerDeadlineText() {
      return this.formatDate(this.product?.deadline_at);
    },
    deliveryDateText() {
      return this.formatDate(this.product?.delivery_date || this.product?.deliver_date || this.product?.deadline_at);
    },
    offersAllowedText() {
      const value = this.product?.offers_allow;
      return Number(value) === 1 || value === true ? 'Yes' : 'No';
    },
    normalizedFields() {
      return Array.isArray(this.product?.data) ? this.product.data : [];
    },
    gridFields() {
      return this.normalizedFields.filter((field) => field && field.type !== 'textarea');
    },
    gridFieldsPairs() {
      const pairs = [];
      for (let i = 0; i < this.gridFields.length; i += 2) {
        pairs.push([this.gridFields[i], this.gridFields[i + 1]]);
      }
      return pairs;
    },
    documents() {
      const docs = Array.isArray(this.product?.documents) ? this.product.documents : [];
      return docs.filter((doc) => doc?.url && !String(doc.url).toLowerCase().match(/\.(png|jpg|jpeg|gif|webp)$/));
    },
    images() {
      const docs = Array.isArray(this.product?.documents) ? this.product.documents : [];
      return docs.filter((doc) => doc?.url && String(doc.url).toLowerCase().match(/\.(png|jpg|jpeg|gif|webp)$/));
    },
    videos() {
      return Array.isArray(this.product?.video) ? this.product.video : [];
    },
    meAvatar() {
      const me = this.$store.getters.getMe;
      return me?.avatar || me?.photo || '';
    },
    isSaved() {
      return Boolean(this.product?.saved);
    },
    isProductApproved() {
      const statusName = String(this.product?.status_name || this.product?.status?.name || '')
        .toLowerCase();
      if (statusName.includes('approved')) return true;
      return Number(this.product?.status) === 2;
    },
    canApproveProduct() {
      return Boolean(this.productId) && !this.isApproving && !this.isProductApproved;
    },
    isCounterOfferCreationLocked() {
      return this.isProductApproved;
    },
    canSubmitCounterOffer() {
      if (this.isCounterOfferCreationLocked) return false;
      const price = Number(this.counterOfferPrice);
      const hasPrice = Number.isFinite(price) && price > 0;
      const hasTerms = Boolean(String(this.counterOfferTerms || '').trim());
      return hasPrice && hasTerms;
    },
  },
  async created() {
    await this.fetchProduct();
  },
  mounted() {
    window.addEventListener('phase2-action-panel-hidden', this.onPhase2ActionPanelHidden);
    window.addEventListener('phase2-action-panel-open-request', this.onProductPanelOpenRequest);
    window.addEventListener('phase2-action-panel-refresh', this.onProductPanelRefresh);
    window.addEventListener('phase2-action-panel-open-request', this.onOfferPanelOpenRequest);
    window.addEventListener('phase2-action-panel-refresh', this.onOfferPanelRefresh);
  },
  beforeDestroy() {
    window.removeEventListener('phase2-action-panel-hidden', this.onPhase2ActionPanelHidden);
    window.removeEventListener('phase2-action-panel-open-request', this.onProductPanelOpenRequest);
    window.removeEventListener('phase2-action-panel-refresh', this.onProductPanelRefresh);
    window.removeEventListener('phase2-action-panel-open-request', this.onOfferPanelOpenRequest);
    window.removeEventListener('phase2-action-panel-refresh', this.onOfferPanelRefresh);
    if (this.successTimer) clearTimeout(this.successTimer);
    this.clearCounterOfferMessageTimer();
  },
  methods: {
    offerBulb(offer) {
      return this.$store.getters.productOfferBulb('buyer', offer && offer.id);
    },
    async markProductMessagesRead(msgIds) {
      const ids = (Array.isArray(msgIds) ? msgIds : [])
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0);
      if (!this.productId || !ids.length) return false;
      try {
        await MatchmakingService.markMarketplaceProductMessagesRead(this.productId, ids);
        return true;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        return false;
      }
    },
    refreshProductThread() {
      if (this.productThreadPromise) {
        this.productThreadPending = true;
        return this.productThreadPromise;
      }

      const promise = Promise.resolve().then(() => this.runProductThreadRefresh());
      this.productThreadPromise = promise;
      const cleanup = () => {
        if (this.productThreadPromise === promise) this.productThreadPromise = null;
      };
      promise.then(cleanup, cleanup);
      return promise;
    },
    async runProductThreadRefresh() {
      const id = this.productId || (this.$route.params && this.$route.params.id);
      let result = { ok: false, msgIds: this.productMsgIds };

      if (id) {
        try {
          const { data } = await MatchmakingService.marketplaceProduct(id);
          const product = marketplaceProductFromResponse(data);
          if (product && Array.isArray(product.messages)) {
            this.messages = [...product.messages];
          }
          result = { ok: true, msgIds: this.productMsgIds };
        } catch (e) {
          result = { ok: false, msgIds: this.productMsgIds };
        }
      }

      if (this.productThreadPending) {
        this.productThreadPending = false;
        result = await this.runProductThreadRefresh();
      }

      return result;
    },
    onProductPanelOpenRequest(e) {
      const detail = e && e.detail;
      if (!detail || detail.panel !== 'seller') return;
      if (!this.$el || !detail.target || !this.$el.contains(detail.target)) return;
      this.pendingProductPanelOpenDetail = detail;
      this.syncProductThreadPanel();
    },
    onProductPanelRefresh(e) {
      const detail = e && e.detail;
      if (!detail || detail.panel !== 'seller') return;
      if (!this.$el || !detail.target || !this.$el.contains(detail.target)) return;
      this.syncProductThreadPanel();
    },
    async syncProductThreadPanel() {
      if (this.isProductThreadSyncing) return;
      this.isProductThreadSyncing = true;
      try {
        const { ok, msgIds } = await this.refreshProductThread();
        const openDetail = this.pendingProductPanelOpenDetail;
        this.pendingProductPanelOpenDetail = null;
        if (!ok) return;

        if (openDetail) {
          const opened = typeof openDetail.open === 'function' && openDetail.open();
          if (!opened) return;
        }

        await this.$nextTick();
        if (!this.isProductPanelVisible()) return;

        const thread = this.$refs.productMessageThread;
        if (thread && typeof thread.scrollToLastMessage === 'function') {
          await thread.scrollToLastMessage();
        }

        await this.markProductMessagesRead(msgIds);
      } finally {
        this.pendingProductPanelOpenDetail = null;
        this.isProductThreadSyncing = false;
      }
    },
    isProductPanelVisible() {
      if (!this.$el || !this.$el.isConnected || !this.$el.querySelector) return false;
      return Boolean(this.$el.querySelector('.action-panel[data-panel="seller"].is-visible'));
    },
    isCounterOfferPanelVisible() {
      if (!this.$el || !this.$el.isConnected || !this.$el.querySelector) return false;
      return Boolean(this.$el.querySelector('.action-panel[data-panel="offer"].is-visible'));
    },
    offerUnreadIds() {
      return (Array.isArray(this.counterOffers) ? this.counterOffers : [])
        .filter((offer) => offer && offer.id
          && this.$store.getters.productOfferBulb('buyer', offer.id) > 0)
        .map((offer) => String(offer.id));
    },
    onOfferPanelOpenRequest(e) {
      const detail = e && e.detail;
      if (!detail || detail.panel !== 'offer') return;
      if (!this.$el || !detail.target || !this.$el.contains(detail.target)) return;
      this.pendingOfferPanelOpenDetail = detail;
      this.syncCounterOfferPanel();
    },
    onOfferPanelRefresh(e) {
      const detail = e && e.detail;
      if (!detail || detail.panel !== 'offer') return;
      if (!this.$el || !detail.target || !this.$el.contains(detail.target)) return;
      this.syncCounterOfferPanel();
    },
    async syncCounterOfferPanel() {
      if (this.isOfferPanelSyncing) return;
      this.isOfferPanelSyncing = true;
      try {
        const unreadIds = this.offerUnreadIds();
        const offers = Array.isArray(this.counterOffers) ? this.counterOffers.slice() : [];
        const results = await Promise.all(offers.map(async (offer) => {
          const loaded = await this.loadOfferDetails(offer, true);
          return { id: String(offer && offer.id), ok: loaded };
        }));

        const openDetail = this.pendingOfferPanelOpenDetail;
        this.pendingOfferPanelOpenDetail = null;

        const anyOk = !offers.length || results.some((item) => item.ok);
        if (!anyOk) return;

        if (openDetail) {
          const opened = typeof openDetail.open === 'function' && openDetail.open();
          if (!opened) return;
        }

        await this.$nextTick();
        if (!this.isCounterOfferPanelVisible()) return;

        await Promise.all(results.map(async ({ id, ok }) => {
          if (!ok) return;
          const fresh = this.counterOffers.find((o) => String(o && o.id) === id);
          if (!fresh) return;
          if (unreadIds.includes(id)) await this.scrollToOfferThread(id);
          await this.markOfferMessagesRead(fresh);
        }));
      } finally {
        this.pendingOfferPanelOpenDetail = null;
        this.isOfferPanelSyncing = false;
      }
    },
    showSuccess(text) {
      this.successText = text;
      if (this.successTimer) clearTimeout(this.successTimer);
      this.successTimer = setTimeout(() => {
        this.successText = '';
        this.successTimer = null;
      }, 4000);
    },
    safeText(value) {
      const text = String(value || '').trim();
      return text || '—';
    },
    formatDate(value) {
      if (!value) return '—';
      const m = moment(value, [
        'DD/MM/YY',
        'DD/MM/YYYY',
        'YYYY-MM-DD',
        'YYYY-MM-DD HH:mm:ss',
        moment.ISO_8601,
      ], true);
      if (m.isValid()) return m.format('DD.MM.YYYY');
      return String(value);
    },
    formatFieldValue(field) {
      if (!field) return '—';
      if (Array.isArray(field.value)) {
        return field.value
          .map((item) => (item && typeof item === 'object'
            ? (item.name || item.value || item.title || '')
            : item))
          .filter(Boolean)
          .join(', ') || '—';
      }
      if (field.value && typeof field.value === 'object') {
        return field.value.name || field.value.value || field.value.title || '—';
      }
      if (field.initValue && typeof field.initValue === 'object') {
        return field.initValue.name || field.initValue.value || field.initValue.title || '—';
      }
      return field.initValue != null
        ? String(field.initValue)
        : (field.value != null ? String(field.value) : '—');
    },
    async fetchProduct() {
      const id = this.$route.params?.id;
      if (!id) return;

      this.isLoading = true;
      try {
        const { data } = await MatchmakingService.marketplaceProduct(id);
        this.product = data || {};
        this.messages = Array.isArray(this.product?.messages) ? [...this.product.messages] : [];
        this.counterOffers = this.cloneOffers(this.product?.offers);

        this.$nextTick(() => {
          const panel = String(this.$route.query.panel || '');
          if (!panel || !this.$el) return;
          const btn = this.$el.querySelector(`[data-toggle-panel="${panel}"]`);
          if (btn) btn.click();
        });
      } catch (e) {
        this.product = {};
        this.messages = [];
        this.counterOffers = [];
        this.handleException(e);
      } finally {
        this.isLoading = false;
      }
    },
    goBack() {
      const query = {
        view: this.currentView,
      };
      if (this.$route.query.marketplace_id) {
        query.marketplace_id = String(this.$route.query.marketplace_id);
      }
      this.$router.push({
        name: 'profileMatchmaking',
        params: { type: 'buyer' },
        query,
      }).catch(() => {});
    },
    async toggleSaved() {
      if (!this.productId) return;
      try {
        if (this.isSaved) {
          await MatchmakingService.unsaveProduct(this.productId);
          this.$set(this.product, 'saved', false);
          this.showSuccess('Product removed from saved');
        } else {
          await MatchmakingService.saveProduct(this.productId);
          this.$set(this.product, 'saved', true);
          this.showSuccess('Product saved');
        }
      } catch (e) {
        this.handleException(e);
      }
    },
    async onApproveClick() {
      if (!this.canApproveProduct) return;

      this.isApproving = true;
      try {
        const { data } = await MatchmakingService.updateMarketplaceProduct(this.productId, {
          status: 2,
        });

        const updatedProduct = marketplaceProductFromResponse(data);
        const status = Number(updatedProduct?.status);
        const normalizedStatus = Number.isFinite(status) && status > 0 ? status : 2;
        const normalizedStatusName = String(
          updatedProduct?.status_name
          || updatedProduct?.status?.name
          || (normalizedStatus === 2 ? 'approved' : this.product?.status_name)
          || '',
        );

        this.$set(this.product, 'status', normalizedStatus);
        this.$set(this.product, 'status_name', normalizedStatusName);
        this.showSuccess('Product approved');
        await this.fetchProduct();
      } catch (e) {
        this.handleException(e);
      } finally {
        this.isApproving = false;
      }
    },
    cloneOffers(val) {
      const src = Array.isArray(val) ? val : [];
      const cloned = JSON.parse(JSON.stringify(src));
      return cloned.map((offer) => ({
        ...offer,
        messages: Array.isArray(offer?.messages) ? offer.messages : [],
        __detailsLoaded: Boolean(Array.isArray(offer?.messages) && offer.messages.length),
        __detailsLoading: false,
        __detailsError: '',
      }));
    },
    async sendProductMessage(text) {
      if (!this.productId) return;

      this.isSendingProductMessage = true;
      try {
        const { data } = await MatchmakingService.marketplaceProductMessage({
          product_id: this.productId,
          text,
        });
        this.messages.push(data || {
          id: `local-${Date.now()}`,
          text,
          created_at: new Date().toISOString(),
          user: this.$store.getters.getMe || {},
        });
      } catch (e) {
        this.handleException(e);
      } finally {
        this.isSendingProductMessage = false;
      }
    },
    async sendOfferMessage(offer, text) {
      if (!offer?.id || !text) return;

      this.sendingOfferId = offer.id;
      try {
        const { data } = await MatchmakingService.marketplaceProductOfferMessage({
          offer_id: offer.id,
          text,
        });
        if (!Array.isArray(offer.messages)) this.$set(offer, 'messages', []);
        offer.messages.push(data || {
          id: `tmp-${Date.now()}`,
          text,
          created_at: new Date().toISOString(),
          user: this.$store.getters.getMe || {},
        });
      } catch (e) {
        this.handleException(e);
      } finally {
        this.sendingOfferId = null;
      }
    },
    onPhase2ActionPanelHidden(e) {
      if (!e?.detail || e.detail.panel !== 'offer') return;
      this.clearCounterOfferMessageTimer();
      this.counterOfferSuccessMessage = '';
      this.counterOfferErrorMessage = '';
    },

    clearCounterOfferMessageTimer() {
      if (this.counterOfferMessageTimerId != null) {
        clearTimeout(this.counterOfferMessageTimerId);
        this.counterOfferMessageTimerId = null;
      }
    },

    scheduleCounterOfferMessageDismiss() {
      this.clearCounterOfferMessageTimer();
      this.counterOfferMessageTimerId = setTimeout(() => {
        this.counterOfferSuccessMessage = '';
        this.counterOfferErrorMessage = '';
        this.counterOfferMessageTimerId = null;
      }, 8000);
    },

    async submitCounterOffer() {
      if (this.isCounterOfferCreationLocked) {
        this.clearCounterOfferMessageTimer();
        this.counterOfferSuccessMessage = '';
        this.counterOfferErrorMessage = 'Counter offers are unavailable after approval.';
        return;
      }
      if (!this.productId || !this.canSubmitCounterOffer || this.isSendingCounterOffer) return;

      this.clearCounterOfferMessageTimer();
      this.isSendingCounterOffer = true;
      this.counterOfferSuccessMessage = '';
      this.counterOfferErrorMessage = '';

      try {
        const res = await MatchmakingService.createMarketplaceProductOffer({
          product_id: this.productId,
          price: Number(this.counterOfferPrice),
          terms: String(this.counterOfferTerms || '').trim(),
        });
        const raw = res?.data;
        const createdOffer = raw && typeof raw === 'object'
          ? (raw.offer || raw.data || raw.item || raw)
          : null;

        this.counterOfferSuccessMessage = 'Counter offer submitted.';
        this.scheduleCounterOfferMessageDismiss();
        if (createdOffer?.id) {
          const exists = this.counterOffers.some((item) => String(item?.id) === String(createdOffer.id));
          if (!exists) {
            this.counterOffers.push({
              ...createdOffer,
              messages: Array.isArray(createdOffer?.messages) ? createdOffer.messages : [],
              __detailsLoaded: Boolean(Array.isArray(createdOffer?.messages) && createdOffer.messages.length),
              __detailsLoading: false,
              __detailsError: '',
            });
          }
        }

        this.counterOfferPrice = '';
        this.counterOfferTerms = '';
      } catch (e) {
        this.counterOfferErrorMessage = 'Failed to submit counter offer.';
        this.handleException(e);
      } finally {
        this.isSendingCounterOffer = false;
      }
    },
    formatOfferPrice(offer) {
      const value = offer?.price ?? offer?.amount ?? offer?.value;
      const currency = offer?.currency || 'euro';
      if (value === null || value === undefined || value === '') return '—';
      return `${value} ${currency}`;
    },
    offerTermsParagraphs(offer) {
      const raw = offer?.terms
        ?? offer?.term
        ?? offer?.detail
        ?? offer?.description
        ?? offer?.text
        ?? '';
      const text = String(raw || '').trim();
      if (!text) return ['—'];
      return text
        .split(/\n\s*\n|\r\n\s*\r\n|\r\s*\r|\n|\r/g)
        .map((item) => item.trim())
        .filter(Boolean);
    },
    async handleLikeProductMessage(msg) {
      if (!msg?.id) return;
      await MatchmakingService.addLikeToMessage(msg.id);
      const item = this.messages.find((message) => message.id === msg.id || message.id === msg._key);
      if (item) {
        this.$set(item, 'liked', true);
        this.$set(item, 'is_liked', true);
        const count = Number(item?.likes_count ?? item?.likes ?? 0) + 1;
        this.$set(item, 'likes_count', count);
        this.$set(item, 'likes', count);
      }
    },
    async handleUnlikeProductMessage(msg) {
      if (!msg?.id) return;
      await MatchmakingService.removeLikeFromMessage(msg.id);
      const item = this.messages.find((message) => message.id === msg.id || message.id === msg._key);
      if (item) {
        this.$set(item, 'liked', false);
        this.$set(item, 'is_liked', false);
        const count = Math.max(0, Number(item?.likes_count ?? item?.likes ?? 1) - 1);
        this.$set(item, 'likes_count', count);
        this.$set(item, 'likes', count);
      }
    },
    async handleLikeOfferMessage(offer, msg) {
      if (!msg?.id || !offer?.messages) return;
      await MatchmakingService.addLikeToMessage(msg.id);
      const item = offer.messages.find((message) => message.id === msg.id || message.id === msg._key);
      if (item) {
        this.$set(item, 'liked', true);
        this.$set(item, 'is_liked', true);
        const count = Number(item?.likes_count ?? item?.likes ?? 0) + 1;
        this.$set(item, 'likes_count', count);
        this.$set(item, 'likes', count);
      }
    },
    async handleUnlikeOfferMessage(offer, msg) {
      if (!msg?.id || !offer?.messages) return;
      await MatchmakingService.removeLikeFromMessage(msg.id);
      const item = offer.messages.find((message) => message.id === msg.id || message.id === msg._key);
      if (item) {
        this.$set(item, 'liked', false);
        this.$set(item, 'is_liked', false);
        const count = Math.max(0, Number(item?.likes_count ?? item?.likes ?? 1) - 1);
        this.$set(item, 'likes_count', count);
        this.$set(item, 'likes', count);
      }
    },
  },
  watch: {
    '$route.params.id': 'fetchProduct',
  },
};
</script>

<style scoped lang="scss">
.project-details__back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
  color: #1d9dd9;
  font-weight: 600;
  font-size: 14px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &-icon {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
  }
}

.buyer-product-details__success {
  color: #0bb86f;
}

.buyer-product-details__error {
  color: #960c15;
}

.buyer-product-details__hint {
  opacity: 0.7;
}

.coffers__item {
  margin-bottom: 20px;
}

.coffers__item:last-child {
  margin-bottom: 0;
}

.text p {
  margin: 0 0 10px 0;
}

.text p:last-child {
  margin-bottom: 0;
}
</style>
